import React from 'react';
import { TFunction } from 'i18next';
import { match } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { IinstallBase } from '../../models';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Oval } from 'react-loader-spinner';
import API from '../../constants/apiEndpoints';
import { msalFetch } from '../auth/Auth-Utils';
import { AxiosRequestConfig } from 'axios';
import ReadingsCard, { IReading } from './ReadingsCard';
import UnitsCard, { IUnit } from './UnitsCard';
import MaintenanceTimerCard, {
    IMaintenanceTimers
} from './MaintenanceTimerCard';
import AssetHistoryCard, { IAssetHistoryItem } from './AssetHistoryCard';
import AlarmsCard, { IAlarm } from './AlarmsCard';
import EventHistoryCard, { IEventHistory } from './EventHistoryCard';
import EquipmentActionsCard from './EquipmentActionsCard';
import GasTypeBadge from './GasTypeBadge';
import { RouteComponentProps } from 'react-router-dom';

interface Iprops extends RouteComponentProps {
    t: TFunction;
    match: match<{ installID: string }>;
    installBase?: IinstallBase;
    loading: boolean;
    selectedFacilityID: string;
}

export interface IGasTypeSegment {
    text: string;
    textColor: string;
    backgroundColor: string;
}

export interface IGasType {
    segments: IGasTypeSegment[];
}

interface IContacts {
    serviceSales: string;
    equipmentSales: string;
    leadTechnician1: string;
    leadTechnician2: string;
    emergencyNumber: string;
    srvRegionName: string | null;
}

interface IEquipmentDetailData {
    equipment: {
        installBaseID: string;
        serialNumber: string;
        nickname: string;
        sapEquipmentNumber: string | null;
        installDate: string | null;
        isConnected: boolean;
        productID: string;
        productName: string;
        productImagePath: string;
        productImage: string | null; // base64 lightbox image
        subcategoryName: string;
        mainCategoryName: string;
        facilityID: string;
        facilityName: string;
        fullLocation: string;
        buildingName: string;
        floorName: string;
        locationName: string | null;
        roomName: string | null;
        gasType: IGasType | null;
        isOnline: boolean;
    };
    contacts: IContacts;
    readings: IReading[];
    readingsUpdatedTimestamp: string | null;
    units: IUnit[];
    maintenanceTimers: IMaintenanceTimers;
    maintenanceActions: any[];
    alarms: IAlarm[];
    eventHistory: IEventHistory[];
    assetHistory: IAssetHistoryItem[];
}

interface Istate {
    equipmentData: IEquipmentDetailData | null;
    initialFacilityID: string | null;
    isLoading: boolean;
}

export default class EquipmentDetail extends React.Component<Iprops, Istate> {
    constructor(props: Iprops) {
        super(props);
        // TODO: Remove mock data and set to null when API is ready
        this.state = {
            equipmentData: null,
            initialFacilityID: null,
            isLoading: false
        };
    }

    componentDidMount() {
        // TODO: Uncomment when ready to use live API data
        this.fetchEquipmentDetail();
        // Store the initial facility ID when the page loads
        this.setState({ initialFacilityID: this.props.selectedFacilityID });
    }

    componentDidUpdate(prevProps: Iprops) {
        const { selectedFacilityID, history } = this.props;
        const { initialFacilityID } = this.state;

        // If facility changed after initial load, redirect to equipment list
        if (
            initialFacilityID &&
            selectedFacilityID !== initialFacilityID &&
            prevProps.selectedFacilityID !== selectedFacilityID
        ) {
            history.push('/dashboard/equipment');
        }
    }

    // TODO: Uncomment when ready to use live API data
    fetchEquipmentDetail = async () => {
        const { match: routeMatch } = this.props;
        const installID = routeMatch.params.installID;

        this.setState({ isLoading: true });

        try {
            const response = await msalFetch(
                API.GET.facility.dashboardEquipmentDetail,
                {
                    method: 'get',
                    params: {
                        installBaseID: installID
                    }
                } as AxiosRequestConfig
            );

            console.log('Equipment Detail Data:', response.data);
            this.setState({ equipmentData: response.data, isLoading: false });
        } catch (error) {
            console.error('Error fetching equipment detail:', error);
            this.setState({ isLoading: false });
        }
    };

    formatDate = (dateString: string | null) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
        });
    };

    getEquipmentTitle = () => {
        const { equipmentData } = this.state;
        const equipment = equipmentData?.equipment;

        if (!equipment) return 'Equipment Detail';

        // If nickname exists, return it
        if (equipment.nickname) {
            return equipment.nickname;
        }

        // Build title from asset fields: MainCategoryName; BuildingName; FloorName; LocationName; RoomName
        const segments = [
            equipment.mainCategoryName,
            equipment.buildingName,
            equipment.floorName,
            equipment.locationName,
            equipment.roomName
        ].filter(segment => segment); // Filter out null/undefined/empty values

        return segments.length > 0
            ? segments.join('; ')
            : equipment.productName || 'Equipment Detail';
    };

    render() {
        const { equipmentData, isLoading } = this.state;
        // Check if there are active alarms to show the red border and alarm badge icon
        const showAlarm =
            equipmentData?.alarms &&
            equipmentData.alarms.length > 0 &&
            equipmentData.alarms.some(alarm => alarm.isActive === true);

        return (
            <div className="equipment-detail-page">
                <div className="back-button-row">
                    <Link to="/dashboard/equipment" className="back-button">
                        <FontAwesomeIcon icon={['far', 'arrow-left']} />
                        My Equipment
                    </Link>
                </div>
                {isLoading || !equipmentData ? (
                    <div className="equipment-detail-container">
                        <div className="loading-container">
                            <Oval
                                color="#ff9738"
                                secondaryColor="#ffcd9e"
                                height={80}
                                width={80}
                            />
                        </div>
                    </div>
                ) : (
                    <div
                        className={`equipment-detail-container${
                            showAlarm ? ' has-alarm' : ''
                        }`}
                    >
                        {showAlarm && (
                            <div className="alarm-icon-badge">
                                <span className="alert-icon">!</span>
                            </div>
                        )}
                        <div className="equipment-header">
                            <h2>
                                {this.getEquipmentTitle()}
                                {equipmentData?.equipment?.gasType && (
                                    <GasTypeBadge
                                        gasType={
                                            equipmentData.equipment.gasType
                                        }
                                    />
                                )}
                            </h2>
                        </div>

                        <div className="equipment-content-layout">
                            <div className="equipment-left-container">
                                <div className="equipment-image-container">
                                    {(equipmentData?.equipment?.productImage ||
                                        equipmentData?.equipment
                                            ?.productImagePath) && (
                                        <img
                                            src={
                                                equipmentData.equipment
                                                    .productImage
                                                    ? `data:image/png;base64,${equipmentData.equipment.productImage}`
                                                    : equipmentData.equipment
                                                          .productImagePath
                                            }
                                            alt={
                                                equipmentData.equipment
                                                    .nickname ||
                                                'Equipment Image'
                                            }
                                        />
                                    )}
                                </div>

                                <div className="equipment-info-section">
                                    <h3>
                                        {equipmentData?.equipment
                                            ?.buildingName || 'Building: N/A'}
                                    </h3>
                                    <h4>
                                        {equipmentData?.equipment?.floorName ||
                                            'Floor: N/A'}
                                    </h4>
                                    <div className="equipment-details">
                                        <p className="detail-item">
                                            <span className="detail-label">
                                                Model No:
                                            </span>{' '}
                                            {equipmentData?.equipment
                                                ?.productName || 'N/A'}
                                        </p>
                                        <p className="detail-item">
                                            <span className="detail-label">
                                                SN:
                                            </span>{' '}
                                            {equipmentData?.equipment
                                                ?.serialNumber || 'N/A'}
                                        </p>
                                        <p className="detail-item">
                                            <span className="detail-label">
                                                Installed:
                                            </span>{' '}
                                            {this.formatDate(
                                                equipmentData?.equipment
                                                    ?.installDate || null
                                            )}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="equipment-right-container">
                                {equipmentData?.readings &&
                                    equipmentData.readings.length > 0 && (
                                        <div className="smartlink-status-row">
                                            <span
                                                className={`smartlink-status-dot${
                                                    equipmentData.equipment
                                                        ?.isOnline
                                                        ? ' smartlink-status-dot--online'
                                                        : ' smartlink-status-dot--offline'
                                                }`}
                                            />
                                            <span className="smartlink-status-label">
                                                {equipmentData.equipment
                                                    ?.isOnline
                                                    ? 'Online'
                                                    : 'Offline'}
                                            </span>
                                        </div>
                                    )}
                                <ReadingsCard
                                    readings={equipmentData?.readings || []}
                                    updatedTimestamp={
                                        equipmentData?.readingsUpdatedTimestamp
                                    }
                                    srvRegionName={
                                        equipmentData?.contacts?.srvRegionName
                                    }
                                />

                                <UnitsCard units={equipmentData?.units || []} />

                                <MaintenanceTimerCard
                                    maintenanceTimers={
                                        equipmentData?.maintenanceTimers || {
                                            system: [],
                                            units: []
                                        }
                                    }
                                />

                                <AlarmsCard
                                    alarms={equipmentData?.alarms || []}
                                />

                                <EventHistoryCard
                                    eventHistory={
                                        equipmentData?.eventHistory || []
                                    }
                                    installBaseID={
                                        equipmentData?.equipment
                                            ?.installBaseID || ''
                                    }
                                />

                                <AssetHistoryCard
                                    assetHistory={
                                        equipmentData?.assetHistory || []
                                    }
                                />

                                <EquipmentActionsCard
                                    t={this.props.t}
                                    installBaseID={
                                        equipmentData?.equipment
                                            ?.installBaseID || ''
                                    }
                                    productID={
                                        equipmentData?.equipment?.productID ||
                                        ''
                                    }
                                    contacts={equipmentData?.contacts}
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}
